import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { studentAPI } from '../../utils/api';
import api from '../../utils/api';

const CVUpload = ({ onSuccess, currentProfile, lang = 'tr' }) => {
  const [status, setStatus] = useState('idle');
  const [result, setResult] = useState(null);
  const [applying, setApplying] = useState(false);

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;
    setStatus('uploading');
    try {
      const { data } = await studentAPI.uploadCV(file, lang);
      setResult(data);
      setStatus('success');
      onSuccess?.(data);
    } catch { setStatus('error'); }
  }, [onSuccess]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    maxFiles: 1,
    disabled: status === 'uploading'
  });

  const applySuggestions = async () => {
    if (!result) return;
    setApplying(true);
    try {
      const prof = await studentAPI.getProfile();
      const current = prof.data || {};
      const mergedSkills = Array.from(new Set([...(current.skills || []), ...(result.detected_skills || [])]));
      const mergedCerts = Array.from(new Set([...(current.certificates || []), ...(result.detected_certificates || [])]));
      const update = {
        skills: mergedSkills,
        certificates: mergedCerts,
      };
      const c = result.detected_contact || {};
      if (!current.phone && c.phone) update.phone = c.phone;
      if (!current.linkedin_url && c.linkedin_url) update.linkedin_url = c.linkedin_url;
      if (!current.github_url && c.github_url) update.github_url = c.github_url;
      const e = result.detected_education || {};
      if (!current.gpa && e.gpa) update.gpa = e.gpa;
      if (!current.university && e.universities?.[0]) update.university = e.universities[0];
      await studentAPI.updateProfile({ ...current, ...update });
      alert('Profilinize uygulandı.');
      onSuccess?.(result);
    } catch { alert('Uygulanamadı'); }
    setApplying(false);
  };

  const Tag = ({ children, color, bg }) => (
    <span style={{ background: bg, color, fontSize: 12, padding: '3px 10px', borderRadius: 20 }}>{children}</span>
  );

  const hasSuggestions = result && (
    (result.detected_skills?.length || 0) > 0 ||
    (result.detected_certificates?.length || 0) > 0 ||
    result.detected_contact?.phone || result.detected_contact?.linkedin_url || result.detected_contact?.github_url ||
    result.detected_education?.gpa || result.detected_education?.universities?.length
  );

  return (
    <div>
      <div {...getRootProps()} style={{
        border: `2px dashed ${isDragActive ? '#2563eb' : '#d1d5db'}`,
        borderRadius: 12, padding: '40px 24px', textAlign: 'center',
        cursor: status === 'uploading' ? 'not-allowed' : 'pointer',
        background: isDragActive ? '#eff6ff' : '#f9fafb', transition: 'all 0.2s'
      }}>
        <input {...getInputProps()} />
        {status === 'uploading' ? <p style={{ color: '#6b7280' }}>Yükleniyor, CV analiz ediliyor...</p>
          : status === 'success' ? <p style={{ color: '#16a34a' }}>✓ CV yüklendi ve analiz edildi</p>
          : (<>
            <p style={{ color: '#374151', fontWeight: 500 }}>{isDragActive ? 'Dosyayı bırakın' : 'CV dosyanızı sürükleyin veya tıklayın'}</p>
            <p style={{ color: '#9ca3af', fontSize: 13, marginTop: 4 }}>PDF veya DOCX • Maks. 5MB • otomatik beceri/sertifika analizi</p>
          </>)}
      </div>

      {status === 'success' && hasSuggestions && (
        <div style={{ marginTop: 16, padding: 16, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#92400e', marginBottom: 10 }}>📋 CV'den Otomatik Çıkarılan Bilgiler</div>

          {result.detected_skills?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Beceriler ({result.detected_skills.length})</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {result.detected_skills.map(s => <Tag key={s} color="#1d4ed8" bg="#dbeafe">{s}</Tag>)}
              </div>
            </div>
          )}

          {result.detected_certificates?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Sertifikalar ({result.detected_certificates.length})</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {result.detected_certificates.map(c => <Tag key={c} color="#059669" bg="#d1fae5">{c}</Tag>)}
              </div>
            </div>
          )}

          {(result.detected_contact?.phone || result.detected_contact?.linkedin_url || result.detected_contact?.github_url) && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>İletişim</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, fontSize: 12, color: '#374151' }}>
                {result.detected_contact.phone && <span>📱 {result.detected_contact.phone}</span>}
                {result.detected_contact.linkedin_url && <span>🔗 LinkedIn</span>}
                {result.detected_contact.github_url && <span>🐙 GitHub</span>}
              </div>
            </div>
          )}

          {(result.detected_education?.gpa || result.detected_education?.universities?.length) && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Eğitim</div>
              <div style={{ fontSize: 12, color: '#374151' }}>
                {result.detected_education.universities?.join(', ')}
                {result.detected_education.gpa && ` • GPA: ${result.detected_education.gpa}`}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button onClick={applySuggestions} disabled={applying} style={{
              padding: '8px 16px', background: '#16a34a', color: '#fff', border: 'none',
              borderRadius: 7, fontSize: 13, fontWeight: 500, cursor: 'pointer'
            }}>{applying ? 'Uygulanıyor...' : '✓ Profile Uygula (boş alanlara ekler)'}</button>
            <button onClick={() => setResult(null)} style={{
              padding: '8px 14px', background: '#f3f4f6', border: '1px solid #d1d5db',
              borderRadius: 7, fontSize: 13, cursor: 'pointer'
            }}>Yoksay</button>
          </div>
        </div>
      )}

      {status === 'error' && <p style={{ color: '#dc2626', fontSize: 13, marginTop: 8 }}>Yükleme başarısız. Lütfen tekrar deneyin.</p>}
    </div>
  );
};

export default CVUpload;
